import { SelectItem as AriaKitSelectItem, SelectState as AriaKitSelectState } from "ariakit";
import React from "react";
import styled from "styled-components";
import { useEvent } from "../hooks/useEvent";
import { Icon } from "../Icon";
import { style } from "../style";
import { hexWithOpacityToRBGA } from "../utils/hexWithOpacityToRBGA";

type SelectItemDetailsProps = {
  title: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  mono: boolean;
  compact: boolean;
  subtitle?: React.ReactNode;
  icon?: string;
};

type SelectItemDetailsContainerProps = {
  $hasIcon: boolean;
  $hasSubtitle: boolean;
  $hasSelected: boolean;
  $compact: boolean;
};

const ICON_SIZE = "24px";

/**
 * Calculates the grid for the select menu item depending on whether
 * other sub-components appear in the component. For example, if there
 * is an icon, we have to create an additional column for the icon.
 */
const gridTemplate = (props: SelectItemDetailsContainerProps) => {
  const gridTemplateColumns = [];
  const gridTemplateAreas = {
    row1: [] as string[],
    row2: [] as string[],
    row3: [] as string[],
    row4: [] as string[],
  };

  if (props.$hasIcon) {
    gridTemplateAreas.row1.push("icon");
    gridTemplateAreas.row2.push("icon");
    gridTemplateAreas.row3.push("icon");
    gridTemplateAreas.row4.push("icon");
    gridTemplateColumns.push(ICON_SIZE);
  }

  if (props.$hasSubtitle) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("subtitle");
    gridTemplateAreas.row4.push(".");
    gridTemplateColumns.push("1fr");
  } else {
    gridTemplateAreas.row1.push("title");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("title");
    gridTemplateAreas.row4.push("title");
    gridTemplateColumns.push("1fr");
  }

  if (props.$hasSelected) {
    gridTemplateAreas.row1.push("selected");
    gridTemplateAreas.row2.push("selected");
    gridTemplateAreas.row3.push("selected");
    gridTemplateAreas.row4.push("selected");
    gridTemplateColumns.push("auto");
  }

  return {
    gridTemplateRows: "auto auto auto auto",
    gridTemplateColumns: gridTemplateColumns.join(" "),
    gridTemplateAreas: `
      '${gridTemplateAreas.row1.join(" ")}'
      '${gridTemplateAreas.row2.join(" ")}'
      '${gridTemplateAreas.row3.join(" ")}'
      '${gridTemplateAreas.row4.join(" ")}'
    `,
    rowGap: props.$compact ? "0" : "4px",
  };
};

const SelectItemDetailsContainer = styled.div`
  all: unset;

  height: 100%;
  display: grid;
  column-gap: 12px;
  padding: 0px 16px;
  ${gridTemplate}
`;

const SelectItemDetailsIconWrapper = styled.div<{ $disabled: boolean }>`
  all: unset;
  grid-area: icon;
  align-self: center;

  opacity: ${(props) => (props.$disabled ? 0.3 : 1)};
`;

const SelectItemDetailsTitleWrapper = styled.div<{ $disabled: boolean; $selected: boolean; $mono: boolean }>`
  all: unset;
  grid-area: title;
  align-self: center;

  color: ${(props) => {
    if (props.$disabled) {
      return hexWithOpacityToRBGA(style.colors.grey.darkest, 0.3);
    }

    if (props.$selected) {
      return style.colors.white;
    }

    return style.colors.grey.darkest;
  }};
  font-size: ${style.vars.fontSize.base};

  ${(props) => {
    return {
      fontFamily: props.$mono ? style.vars.monoFont : undefined,
      fontWeight: props.$mono ? undefined : style.vars.fontWeight.bold,
    };
  }}
`;

const SelectItemDetailsSubtitleWrapper = styled.div<{ $disabled: boolean; $selected: boolean; $mono: boolean }>`
  all: unset;
  grid-area: subtitle;
  align-self: center;

  color: ${(props) => (props.$selected && !props.$disabled ? style.colors.grey.light : style.colors.grey.dark)};
  font-weight: ${style.vars.fontWeight.base};
  font-size: ${style.vars.fontSize.small};

  ${(props) => {
    return { fontFamily: props.$mono ? style.vars.monoFont : undefined };
  }}
`;

const SelectItemDetailsSelectedWrapper = styled.div`
  all: unset;
  grid-area: selected;
  align-self: center;

  color: ${style.colors.white};
`;

const SelectItemDetails: React.FC<SelectItemDetailsProps> = (props) => {
  const { title, selected, disabled, subtitle = null, mono, compact, icon = null } = props;

  return (
    <SelectItemDetailsContainer $compact={compact} $hasIcon={icon !== null} $hasSubtitle={subtitle !== null} $hasSelected={selected}>
      {icon !== null && (
        <SelectItemDetailsIconWrapper $disabled={disabled}>
          <Icon icon={icon} xlarge />
        </SelectItemDetailsIconWrapper>
      )}

      <SelectItemDetailsTitleWrapper $disabled={disabled} $selected={selected} $mono={mono}>
        {title}
      </SelectItemDetailsTitleWrapper>

      {subtitle !== null && (
        <SelectItemDetailsSubtitleWrapper $disabled={disabled} $selected={selected} $mono={mono}>
          {subtitle}
        </SelectItemDetailsSubtitleWrapper>
      )}

      {selected && (
        <SelectItemDetailsSelectedWrapper>
          <Icon aria-hidden icon="check" />
        </SelectItemDetailsSelectedWrapper>
      )}
    </SelectItemDetailsContainer>
  );
};

const SELECT_ITEM_HEIGHT = 57;
const SELECT_ITEM_COMPACT_HEIGHT = 48;

const SelectItemContainer = styled(AriaKitSelectItem)<{ $selected: boolean }>`
  all: unset;

  display: block;
  cursor: pointer;

  width: 100%;
  height: 100%;

  ${(props) => {
    if (props.$selected && !props.disabled) {
      return {
        background: style.colors.grey.dark,
      };
    }
  }}

  :hover {
    ${(props) => {
      if (!props.$selected) {
        return {
          background: style.colors.grey.light,
        };
      }
    }}
  }

  :focus,
  :focus-within {
    outline: ${style.vars.borderWidth.base} dotted ${(props) => (props.$selected ? style.colors.white : style.colors.grey.dark)};
    outline-offset: -3px;
  }
`;

type Option = {
  value: string;
  title: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  subtitle?: React.ReactNode;
  icon?: string;
};

type Props = {
  state: AriaKitSelectState;
  mono: boolean;
  compact: boolean;
  option: Option;
  "data-testid"?: string;
};

export const SelectItem = (props: Props): JSX.Element => {
  const { state, option, mono, compact } = props;
  const { value, title, selected, disabled, subtitle, icon } = option;

  /**
   * This is a hack to get around a bug in ariakit where the internal focus
   * event is not persisting the React event object. This causes React to release
   * the event and throw an error when it is accessed.
   */
  const onFocus = useEvent<React.FocusEventHandler<HTMLDivElement>>((ev) => {
    ev.persist();
  });

  return (
    <SelectItemContainer
      state={state}
      id={`${value}-select-menu-item`}
      tabIndex={0}
      $selected={selected}
      disabled={disabled}
      value={value}
      focusOnHover={false}
      preventScrollOnKeyDown
      onFocus={onFocus}
      data-testid={props["data-testid"]}
    >
      <SelectItemDetails
        title={title}
        subtitle={subtitle}
        icon={icon}
        mono={mono}
        selected={selected}
        disabled={disabled}
        compact={compact}
      />
    </SelectItemContainer>
  );
};

SelectItem.displayName = "SelectItem";

/**
 * Calculates the height of the select item based on the state of the compact prop.
 * @private
 */
SelectItem.__INTERNAL__fastCalculateOuterHeight = (props: Pick<Props, "compact">): number => {
  return props.compact ? SELECT_ITEM_COMPACT_HEIGHT : SELECT_ITEM_HEIGHT;
};
